import React, { useState, useEffect, useCallback, useRef } from 'react';
import MonthlyVacationDeclaration from '../../components/employee/MonthlyVacationDeclaration';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiX, FiTrash2, FiCheck } from 'react-icons/fi';
import { timeEntryService } from '../../services/api';
import { formatHoursMinutes, formatMinutes } from '../../utils/formatHours';

/* ---------- palette (REQ-EMP #9) ---------- */
const C = { green: 'var(--act-approve)', red: 'var(--act-danger)', action: 'var(--act-primary)', yellow: 'var(--status-warning)', ink: 'var(--ve-ink-800)', muted: 'var(--ve-ink-600)', line: 'var(--border-subtle)', bg: 'var(--surface-subtle)' };
const STATUS_COLOR = { approved: C.green, off: C.green, challenged: C.red, pending: C.action, missing: C.yellow, allocation_required: C.yellow };

/* ---------- styled ---------- */
const Page = styled.div`max-width: 900px;`;
const Bar = styled.div`display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap;margin-bottom:.25rem;`;
const Nav = styled.div`display:flex;align-items:center;gap:.5rem;`;
const NavBtn = styled.button`display:inline-flex;align-items:center;padding:.4rem .6rem;border:1px solid ${C.line};border-radius:.3rem;background:#fff;cursor:pointer;color:${C.ink};&:disabled{opacity:.4;cursor:not-allowed;}&:hover:not(:disabled){background:#f0f3f6;}`;
const MonthLabel = styled.span`font-weight:700;color:${C.ink};min-width:170px;text-align:center;text-transform:capitalize;font-size:1.05rem;`;
const CustomerName = styled.div`color:${C.muted};font-size:.95rem;margin:0 0 1rem;`;
const Strong = styled.span`color:${C.ink};font-weight:600;`;
const Legend = styled.div`display:flex;gap:1rem;flex-wrap:wrap;margin:0 0 1rem;font-size:.8rem;color:${C.muted};`;
const Dot = styled.span`display:inline-block;width:11px;height:11px;border-radius:3px;margin-right:.35rem;vertical-align:middle;background:${({ c }) => c};`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);gap:6px;`;
const Wd = styled.div`text-align:center;font-size:.72rem;color:${C.muted};font-weight:600;text-transform:uppercase;padding:.2rem 0;`;
const Cell = styled.button`position:relative;min-height:70px;border:1px solid ${C.line};border-radius:.4rem;background:#fff;text-align:left;padding:.35rem .4rem;cursor:${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  ${({ status }) => (status && STATUS_COLOR[status] ? `border-left:4px solid ${STATUS_COLOR[status]};background:${STATUS_COLOR[status]}12;` : '')}
  &:hover{ ${({ $clickable }) => ($clickable ? 'filter:brightness(0.97);' : '')} }`;
const Blank = styled.div``;
const DayNum = styled.div`font-weight:600;color:${C.ink};font-size:.85rem;`;
const DayHours = styled.div`position:absolute;bottom:.35rem;right:.45rem;font-weight:700;color:${C.ink};font-size:.9rem;`;
const OffTag = styled.div`position:absolute;bottom:.35rem;right:.45rem;font-size:.72rem;color:${C.green};font-weight:600;`;
const Alert = styled.div`background:var(--status-danger-plate);color:${C.red};border:1px solid #f5c6cb;padding:.6rem .9rem;border-radius:.3rem;margin-bottom:1rem;font-size:.9rem;`;
const Empty = styled.div`padding:2rem;text-align:center;color:${C.muted};`;

/* modal */
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;`;
const Dialog = styled.div`background:#fff;border-radius:.6rem;width:520px;max-width:100%;max-height:90vh;overflow:auto;padding:1.3rem;`;
const DlgHead = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:.4rem;`;
const DlgTitle = styled.h3`margin:0;color:${C.ink};font-size:1.1rem;`;
const IconBtn = styled.button`background:none;border:none;color:${C.muted};cursor:pointer;font-size:1.2rem;display:inline-flex;`;
const Btns = styled.div`display:flex;gap:.5rem;flex-wrap:wrap;margin:.8rem 0;`;
const ActBtn = styled.button`padding:.5rem .8rem;border-radius:.3rem;font-size:.85rem;font-weight:600;cursor:pointer;border:1px solid ${({ $c }) => $c};background:#fff;color:${({ $c }) => $c};&:hover{background:${({ $c }) => $c}12;}`;
const Rows = styled.div`margin:.5rem 0;`;
const RowHead = styled.div`font-size:.78rem;color:${C.muted};font-weight:600;text-transform:uppercase;margin:.6rem 0 .3rem;`;
const Row = styled.div`display:flex;align-items:center;gap:.5rem;margin:.3rem 0;`;
const TimeInput = styled.input`width:110px;padding:.35rem .4rem;border:1px solid ${C.line};border-radius:.3rem;font-size:.9rem;`;
const Sep = styled.span`color:${C.muted};`;
const Total = styled.div`font-weight:700;color:${C.ink};margin:.7rem 0;`;
const FreeNote = styled.div`background:#eafaf1;border:1px solid #bfe6cf;color:#1e7a46;padding:.6rem .9rem;border-radius:.3rem;margin:.6rem 0;display:flex;justify-content:space-between;align-items:center;gap:.5rem;`;
const ErrBox = styled.div`background:var(--status-danger-plate);border:1px solid #f5c6cb;color:${C.red};padding:.6rem .9rem;border-radius:.3rem;margin:.6rem 0;font-size:.85rem;`;
const ErrList = styled.ul`margin:.3rem 0 0;padding-left:1.1rem;`;
const Foot = styled.div`display:flex;justify-content:flex-end;gap:.6rem;margin-top:1rem;`;
const SaveBtn = styled.button`padding:.55rem 1.2rem;border-radius:.3rem;font-weight:600;border:none;color:#fff;background:${C.action};cursor:pointer;&:disabled{opacity:.5;cursor:not-allowed;}`;
const GhostBtn = styled.button`padding:.55rem 1.2rem;border-radius:.3rem;font-weight:600;border:1px solid ${C.line};background:#fff;color:${C.ink};cursor:pointer;`;
// "Finished" (REQ #1): a compact SQUARE green ✓ button on the LEFT — declares the
// day fully reported and notifies the customer. Square (not a wide rectangle) so
// on a phone it never pushes Save off-screen. The label lives in title/aria-label.
const FinishBtn = styled.button`flex:0 0 auto;width:46px;height:46px;padding:0;border-radius:.3rem;font-weight:700;border:none;color:#fff;background:${C.green};cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:1.4rem;line-height:1;&:disabled{opacity:.5;cursor:not-allowed;}`;
const FinishedMark = styled.div`font-size:.8rem;color:${C.green};font-weight:600;margin-top:.5rem;`;
const MonthTotal = styled.div`color:${C.ink};font-weight:600;font-size:.95rem;`;
const LockNote = styled.div`background:#eef7ff;border:1px solid #cfe4fb;color:#1c4b73;padding:.6rem .9rem;border-radius:.3rem;margin:.6rem 0;font-size:.85rem;`;
/* contest resolution (REQ #13) */
const ContestBox = styled.div`background:var(--status-danger-plate);border:1px solid #f5c6cb;border-radius:.4rem;padding:.8rem .9rem;margin:.4rem 0 .8rem;`;
const ContestReason = styled.div`color:${C.ink};font-size:.92rem;margin:.5rem 0 .2rem;white-space:pre-wrap;font-style:italic;`;
const ProposedHours = styled.div`font-weight:700;color:${C.red};font-size:1.02rem;margin:.2rem 0;`;
const CommentArea = styled.textarea`width:100%;min-height:64px;padding:.5rem;border:1px solid ${C.line};border-radius:.3rem;font-size:.9rem;resize:vertical;font-family:inherit;box-sizing:border-box;`;
const TransNote = styled.div`font-size:.72rem;color:${C.muted};margin-top:.2rem;`;

const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const toMin = (v) => { const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(v || '').trim()); return m ? +m[1] * 60 + +m[2] : null; };

// Client-side worked-minutes estimate for live display (server is authoritative).
const estimateMinutes = (st) => {
  if (st.dayOff) return 0;
  const ov = (a1, a2, b1, b2) => Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));
  const w = st.work.map((r) => [toMin(r.start), toMin(r.end)]).filter((r) => r[0] != null && r[1] != null && r[1] > r[0]);
  const b = st.breaks.map((r) => [toMin(r.start), toMin(r.end)]).filter((r) => r[0] != null && r[1] != null && r[1] > r[0]);
  // Merge overlapping/duplicate breaks so their time is only counted once
  // (mirrors the server's computeWorkday).
  const merged = [...b].sort((x, y) => x[0] - y[0]).reduce((acc, iv) => {
    const last = acc[acc.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
    else acc.push([iv[0], iv[1]]);
    return acc;
  }, []);
  let m = w.reduce((s, r) => s + (r[1] - r[0]), 0);
  merged.forEach((br) => w.forEach((iv) => { m -= ov(iv[0], iv[1], br[0], br[1]); }));
  return Math.max(0, m);
};

function DayEntryModal({ date, today, onClose, onSaved, t, lang }) {
  const [state, setState] = useState({ dayOff: false, work: [], breaks: [] });
  const [locked, setLocked] = useState(false);
  // Authoritative hours from the server (used for the locked Total, since a
  // settled/adjusted day may have no interval breakdown).
  const [serverHours, setServerHours] = useState(0);
  const [errors, setErrors] = useState([]);
  // null while idle; 'save' or 'finished' identifies which button is in flight.
  const [savingKind, setSavingKind] = useState(null);
  const [finished, setFinished] = useState(false);
  const [allocation, setAllocation] = useState(null);
  const [choices, setChoices] = useState([]);
  const [selection, setSelection] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [historical, setHistorical] = useState(false);
  const reportOperation = useRef(null);
  const [loading, setLoading] = useState(true);
  // Contest resolution (REQ #13)
  const [status, setStatus] = useState(null);
  const [contest, setContest] = useState(null);
  const [disagreeing, setDisagreeing] = useState(false);
  const [comment, setComment] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const d = await timeEntryService.getDay(date, lang, selection || {});
        if (!active) return;
        // The server supplies all eligible identities on inclusive boundary D.
        // Keep an empty choice until the employee explicitly selects a customer.
        if (d.requiresPlacementSelection) setChoices(d.choices || []);
        setCustomerName(d.customer?.name || '');
        setHistorical(Boolean(d.historical));
        const seg = d.segments || {};
        setState({ dayOff: !!seg.dayOff, work: seg.work || [], breaks: seg.breaks || [] });
        setLocked(!!d.locked);
        setServerHours(Number(d.hours) || 0);
        setStatus(d.status || null);
        setContest(d.contest || null);
        setFinished(!!d.finished);
        setAllocation(d.allocation || null);
      } catch (e) {
        if (active) { setLocked(true); setErrors([e.message]); }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [date, lang, selection]);

  const upd = (fn) => setState((s) => fn({ ...s, work: [...s.work], breaks: [...s.breaks] }));
  const startEvent = (kind) => upd((s) => { s.dayOff = false; s[kind] = [...s[kind], { start: nowHHMM(), end: '' }]; return s; });
  const stopEvent = (kind) => upd((s) => {
    s.dayOff = false;
    const open = s[kind].map((x, idx) => ({ x, idx })).reverse().find((o) => !o.x.end);
    if (open) s[kind][open.idx] = { ...s[kind][open.idx], end: nowHHMM() };
    else s[kind] = [...s[kind], { start: '', end: nowHHMM() }];
    return s;
  });
  const editRow = (kind, i, field, v) => upd((s) => { s[kind][i] = { ...s[kind][i], [field]: v }; return s; });
  const removeRow = (kind, i) => upd((s) => { s[kind] = s[kind].filter((_, j) => j !== i); return s; });
  const setFree = () => setState({ dayOff: true, work: [], breaks: [] });
  const clearAll = () => setState({ dayOff: false, work: [], breaks: [] });

  // Same-day guard (feedback #15): mirror the backend's INVALID_DAY future-time
  // check so the employee gets instant feedback when saving TODAY. The clock is
  // read fresh at save time; past days are never restricted (backend stays
  // authoritative — it runs the same check in APP_TZ).
  const futureTimeErrors = () => {
    if (!today || date !== today) return [];
    // "Now" on the company clock (Europe/Copenhagen), matching the backend's
    // APP_TZ check — a device set to another timezone must not falsely block
    // (or falsely pass) times the server will judge in Copenhagen wall time.
    const hhmm = new Date().toLocaleTimeString('en-GB', {
      timeZone: 'Europe/Copenhagen',
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
    });
    const [nh, nm] = hhmm.split(':').map(Number);
    const nowMin = nh * 60 + nm;
    const errs = [];
    [
      ['work', t('employee.cal.rowWork', { defaultValue: 'Work period' })],
      ['breaks', t('employee.cal.rowBreak', { defaultValue: 'Break' })],
    ].forEach(([kind, label]) => {
      state[kind].forEach((r, i) => {
        const s = toMin(r.start);
        const e = toMin(r.end);
        if (s !== null && s > nowMin) errs.push(t('employee.cal.futureStart', { defaultValue: '{{label}} {{n}}: the start time ({{time}}) is in the future — you can only log time that has already happened.', label, n: i + 1, time: r.start }));
        if (e !== null && e > nowMin) errs.push(t('employee.cal.futureStop', { defaultValue: '{{label}} {{n}}: the stop time ({{time}}) is in the future — you can only log time that has already happened.', label, n: i + 1, time: r.end }));
      });
    });
    return errs;
  };

  // finished=false → plain "Save" (keeps whatever finished state the day had).
  // finished=true  → "Finished": stamps the day submitted + notifies the customer.
  const save = async (asFinished = false) => {
    const pre = futureTimeErrors();
    if (pre.length) { setErrors(pre); return; }
    setSavingKind(asFinished ? 'finished' : 'save'); setErrors([]);
    try {
      const payload = { date, dayOff: state.dayOff, work: state.work, breaks: state.breaks,
        finished: asFinished, ...(selection || {}), ...(allocation ? { expectedReportRevision: allocation.revision } : {}) };
      const key = JSON.stringify(payload);
      if (reportOperation.current?.key !== key) reportOperation.current = { key, operationId: window.crypto.randomUUID() };
      const saved = await timeEntryService.saveDay({ ...payload, operationId: reportOperation.current.operationId });
      if (saved.allocation) {
        setAllocation(saved.allocation); setFinished(asFinished || finished); setStatus('allocation_required');
        return;
      }
      onSaved();
    } catch (e) {
      setErrors(e.details && e.details.length ? e.details : [e.message]);
    } finally { setSavingKind(null); }
  };

  // Contest: agree adopts the customer's hours (→green); disagree needs a note.
  const resolve = async (agree) => {
    setResolving(true); setErrors([]);
    try {
      await timeEntryService.resolveDay({ date, agree, comment: agree ? undefined : comment, ...(selection || {}) });
      onSaved();
    } catch (e) {
      setErrors([e.message]);
    } finally { setResolving(false); }
  };

  const totalMin = estimateMinutes(state);
  const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const renderRows = (kind, label) => (
    <>
      <RowHead>{label}</RowHead>
      {state[kind].length === 0 && <div style={{ color: C.muted, fontSize: '.85rem' }}>—</div>}
      {state[kind].map((r, i) => (
        <Row key={i}>
          <TimeInput type="time" value={r.start} disabled={locked} onChange={(e) => editRow(kind, i, 'start', e.target.value)} />
          <Sep>→</Sep>
          <TimeInput type="time" value={r.end} disabled={locked} onChange={(e) => editRow(kind, i, 'end', e.target.value)} />
          {!locked && <IconBtn onClick={() => removeRow(kind, i)} title={t('common.delete', { defaultValue: 'Remove' })}><FiTrash2 /></IconBtn>}
        </Row>
      ))}
    </>
  );

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <DlgHead>
          <DlgTitle style={{ textTransform: 'capitalize' }}>{dateLabel}</DlgTitle>
          <IconBtn onClick={onClose}><FiX /></IconBtn>
        </DlgHead>
        {choices.length > 0 && <LockNote>
          <label htmlFor="work-customer">{t('employee.cal.chooseCustomer', { defaultValue: 'Choose the customer for this work' })}</label>
          <select id="work-customer" value={selection ? `${selection.customerId}:${selection.placementId || ''}` : ''}
            disabled={loading || Boolean(savingKind) || resolving} style={{ display: 'block', width: '100%', marginTop: '.4rem', padding: '.5rem' }}
            onChange={event => {
              const option = choices.find(row => `${row.customerId}:${row.placementId || ''}` === event.target.value);
              setLoading(true); setErrors([]); setDisagreeing(false); setComment(''); reportOperation.current = null;
              setSelection(option ? { customerId: option.customerId, ...(option.placementId ? { placementId: option.placementId } : {}) } : null);
            }}>
            <option value="">{t('employee.cal.selectCustomer', { defaultValue: 'Select a customer' })}</option>
            {choices.map(option => <option key={`${option.customerId}:${option.placementId || ''}`} value={`${option.customerId}:${option.placementId || ''}`}>
              {option.customerName}{option.hours ? ` (${formatHoursMinutes(option.hours)})` : ''}
            </option>)}
          </select>
        </LockNote>}
        {customerName && <CustomerName>{customerName}</CustomerName>}
        {allocation && <LockNote role="status">{t('workAllocation.saved', { defaultValue: 'Your work is saved. Staff must review its employment link before payroll. You do not need to enter it again.' })}</LockNote>}
        {loading ? <Empty>{t('common.loading', { defaultValue: 'Loading…' })}</Empty> : choices.length > 0 && !selection ? (
          <LockNote>{t('employee.cal.customerRequired', { defaultValue: 'This day has more than one employment. Select the customer before entering or reviewing work.' })}</LockNote>
        ) : locked ? (
          <>
            {!allocation && <LockNote>{historical
              ? t('employee.cal.retainedHistory', { defaultValue: 'This work is retained from an earlier employment. Contact staff if its allocation needs review.' })
              : t('employee.cal.locked', { defaultValue: 'This day is approved and can no longer be changed.' })}</LockNote>}
            {errors.length > 0 && <ErrBox>{errors.join(' ')}</ErrBox>}
            {state.dayOff ? <FreeNote><span>{t('employee.cal.freeNote', { defaultValue: 'Marked as Free — 0 hours.' })}</span></FreeNote> : renderRows('work', t('employee.cal.work', { defaultValue: 'Work periods' }))}
            {!state.dayOff && state.breaks.length > 0 && renderRows('breaks', t('employee.cal.breaks', { defaultValue: 'Breaks' }))}
            {/* Authoritative server hours — a settled/adjusted day may have no interval breakdown.
                Shown as H:MM; the "hours" unit word is dropped because "7:30 hours" reads wrong. */}
            <Total>{t('employee.cal.total', { defaultValue: 'Total' })}: {formatHoursMinutes(serverHours, '0:00')}</Total>
            <Foot><GhostBtn onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</GhostBtn></Foot>
          </>
        ) : (status === 'challenged' && contest) ? (
          <>
            <ContestBox>
              <RowHead style={{ marginTop: 0 }}>{t('employee.cal.contestTitle', { defaultValue: 'The customer contested this day' })}</RowHead>
              <ProposedHours>
                {t('employee.cal.proposedHours', { defaultValue: 'Hours the customer approves' })}: {formatHoursMinutes(contest.proposedHours)}
              </ProposedHours>
              <div style={{ fontSize: '.85rem', color: C.muted }}>
                {t('employee.cal.yourLogged', { defaultValue: 'You logged' })}: {formatMinutes(totalMin)}
              </div>
              {contest.reasonTranslated ? <ContestReason>“{contest.reasonTranslated}”</ContestReason> : null}
              {contest.translated ? <TransNote>{t('employee.cal.translated', { defaultValue: 'Translated for you' })}</TransNote> : null}
            </ContestBox>

            {disagreeing && (
              <>
                <RowHead>{t('employee.cal.disagreeReason', { defaultValue: 'Why do you disagree?' })}</RowHead>
                <CommentArea value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder={t('employee.cal.disagreePh', { defaultValue: 'Explain briefly — an administrator will decide.' })} />
              </>
            )}

            {errors.length > 0 && <ErrBox>{errors.map((er, i) => <div key={i}>{er}</div>)}</ErrBox>}

            <Foot>
              {disagreeing ? (
                <>
                  <GhostBtn onClick={() => { setDisagreeing(false); setComment(''); setErrors([]); }}>{t('common.back', { defaultValue: 'Back' })}</GhostBtn>
                  <SaveBtn style={{ background: C.red }} onClick={() => resolve(false)} disabled={resolving || !comment.trim()}>
                    {resolving ? t('common.saving', { defaultValue: 'Saving…' }) : t('employee.cal.submitDisagree', { defaultValue: 'Submit disagreement' })}
                  </SaveBtn>
                </>
              ) : (
                <>
                  <GhostBtn onClick={() => setDisagreeing(true)}>{t('employee.cal.disagree', { defaultValue: 'Disagree' })}</GhostBtn>
                  <SaveBtn style={{ background: C.green }} onClick={() => resolve(true)} disabled={resolving}>
                    {resolving ? t('common.saving', { defaultValue: 'Saving…' }) : t('employee.cal.agree', { defaultValue: 'OK — accept these hours' })}
                  </SaveBtn>
                </>
              )}
            </Foot>
          </>
        ) : (
          <>
            <Btns>
              <ActBtn $c={C.green} onClick={() => startEvent('work')}>{t('employee.cal.workStart', { defaultValue: 'Work start' })}</ActBtn>
              <ActBtn $c={C.ink} onClick={() => stopEvent('work')}>{t('employee.cal.workStop', { defaultValue: 'Work stop' })}</ActBtn>
              <ActBtn $c={C.action} onClick={() => startEvent('breaks')}>{t('employee.cal.breakStart', { defaultValue: 'Break start' })}</ActBtn>
              <ActBtn $c={C.muted} onClick={() => stopEvent('breaks')}>{t('employee.cal.breakStop', { defaultValue: 'Break stop' })}</ActBtn>
              <ActBtn $c={C.yellow} onClick={setFree}>{t('employee.cal.free', { defaultValue: 'Free (off duty)' })}</ActBtn>
            </Btns>

            {state.dayOff ? (
              <FreeNote>
                <span>{t('employee.cal.freeNote', { defaultValue: 'Marked as Free — 0 hours.' })}</span>
                <GhostBtn onClick={clearAll}>{t('employee.cal.undo', { defaultValue: 'Undo' })}</GhostBtn>
              </FreeNote>
            ) : (
              <Rows>
                {renderRows('work', t('employee.cal.work', { defaultValue: 'Work periods' }))}
                {renderRows('breaks', t('employee.cal.breaks', { defaultValue: 'Breaks (within work)' }))}
                <Total>{t('employee.cal.total', { defaultValue: 'Total' })}: {formatMinutes(totalMin)}</Total>
              </Rows>
            )}

            {!state.dayOff && (
              <div style={{ fontSize: '.78rem', color: C.muted, marginTop: '.2rem' }}>
                {t('employee.cal.partialHint', { defaultValue: 'You can save an unfinished day (e.g. just a start) and come back later to add the stop.' })}
              </div>
            )}

            <div style={{ fontSize: '.78rem', color: C.muted, marginTop: '.4rem' }}>
              {t('employee.cal.finishedHint', { defaultValue: 'Press "Finished" once the whole day is reported — the customer is notified to approve it.' })}
            </div>
            {finished && (
              <FinishedMark>✓ {t('employee.cal.finishedMark', { defaultValue: 'You marked this day as finished.' })}</FinishedMark>
            )}

            {errors.length > 0 && (
              <ErrBox>
                <strong>{t('employee.cal.fixErrors', { defaultValue: 'Please fix before saving:' })}</strong>
                <ErrList>{errors.map((er, i) => <li key={i}>{er}</li>)}</ErrList>
              </ErrBox>
            )}

            <Foot style={{ justifyContent: 'space-between' }}>
              <FinishBtn onClick={() => save(true)} disabled={savingKind !== null}
                title={t('employee.cal.finished', { defaultValue: 'Finished — submit day' })}
                aria-label={t('employee.cal.finished', { defaultValue: 'Finished — submit day' })}>
                {savingKind === 'finished' ? '…' : <FiCheck />}
              </FinishBtn>
              <div style={{ display: 'flex', gap: '.6rem' }}>
                <GhostBtn onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</GhostBtn>
                <SaveBtn onClick={() => save(false)} disabled={savingKind !== null}>{savingKind === 'save' ? t('common.saving', { defaultValue: 'Saving…' }) : t('employee.cal.save', { defaultValue: 'Save' })}</SaveBtn>
              </div>
            </Foot>
          </>
        )}
      </Dialog>
    </Overlay>
  );
}

const CalendarView = () => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalDate, setModalDate] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await timeEntryService.getCalendarMonth(year, month)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const nextBlocked = year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
  const shift = (delta) => {
    let m = month + delta; let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1)) return; // no future months
    setYear(y); setMonth(m);
  };

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday-first
  const days = data ? data.days : [];
  const weekdays = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(i18n.language, { weekday: 'short' })); // Jan 1 2024 = Monday

  return (
    <Page>
      <Bar>
        <Nav>
          <NavBtn onClick={() => shift(-1)}><FiChevronLeft /></NavBtn>
          <MonthLabel>{monthLabel}</MonthLabel>
          <NavBtn onClick={() => shift(1)} disabled={nextBlocked}><FiChevronRight /></NavBtn>
        </Nav>
        {data && (
          <MonthTotal>
            {t('employee.cal.monthTotal', { defaultValue: 'This month' })}: <Strong>{formatMinutes(data.monthMinutes, '0:00')}</Strong>
          </MonthTotal>
        )}
      </Bar>
      <MonthlyVacationDeclaration year={year} month={month} />
      <CustomerName>
        {data && data.customer
          ? <>{t('employee.cal.customer', { defaultValue: 'Customer' })}: <Strong>{data.customer.name}</Strong></>
          : (data && !loading ? t('workAllocation.canReport', { defaultValue: 'You can still record your work. Staff will review the employment link.' }) : '')}
      </CustomerName>

      {error && <Alert>{error}</Alert>}

      <Legend>
        <span><Dot c={C.green} />{t('employee.cal.legApproved', { defaultValue: 'Approved' })}</span>
        <span><Dot c={C.action} />{t('employee.cal.legPending', { defaultValue: 'Awaiting approval' })}</span>
        <span><Dot c={C.red} />{t('employee.cal.legChallenged', { defaultValue: 'Contested' })}</span>
        <span><Dot c={C.yellow} />{t('employee.cal.legMissing', { defaultValue: 'Not saved' })}</span>
        <span><Dot c={C.yellow} />{t('workAllocation.title', { defaultValue: 'Allocation review' })}</span>
        <span style={{ color: C.green, fontWeight: 600 }}>✓ {t('employee.cal.legFinished', { defaultValue: 'Reported (finished)' })}</span>
      </Legend>

      {loading ? <Empty>{t('common.loading', { defaultValue: 'Loading…' })}</Empty> : (
        <Grid>
          {weekdays.map((w, i) => <Wd key={i}>{w}</Wd>)}
          {Array.from({ length: firstWeekday }).map((_, i) => <Blank key={`b${i}`} />)}
          {days.map((d) => {
            const clickable = d.status !== 'future';
            return (
              <Cell key={d.date} status={d.status} $clickable={clickable} disabled={!clickable}
                onClick={() => clickable && setModalDate(d.date)}>
                <DayNum>{parseInt(d.date.slice(-2), 10)}{d.finished ? <span style={{ color: C.green, marginLeft: 4 }}>✓</span> : null}</DayNum>
                {d.status === 'off' ? <OffTag>{t('employee.cal.freeShort', { defaultValue: 'Free' })}</OffTag>
                  : d.minutes > 0 ? <DayHours>{formatMinutes(d.minutes)}</DayHours> : null}
              </Cell>
            );
          })}
        </Grid>
      )}

      {modalDate && (
        <DayEntryModal date={modalDate} today={data ? data.today : null} t={t} lang={i18n.language}
          onClose={() => setModalDate(null)} onSaved={() => { setModalDate(null); load(); }} />
      )}
    </Page>
  );
};

export default CalendarView;
